/**
 * One asset, editable.
 *
 * `POST /api/assets`, `PUT /api/assets/:id` and `DELETE /api/assets/:id` have
 * been audited, tested and reachable since 2026-08-20, and until now no screen
 * called any of them. The fleet held 369 assets that the product could describe
 * in four tabs and could not change in one field. This is the other half, the
 * same way `Sites.tsx` was the other half of the site routes.
 *
 * ── 🔴 Why this fetches GET /api/assets/:id and not the detail payload ────
 *
 * The drawer already has the asset — `/api/assets/:id/detail` returned it — and
 * building the form on that object is the obvious thing to do. It would also
 * silently destroy data on the first save.
 *
 * `PUT /api/assets/:id` is a FULL REPLACE: it writes `site_id`,
 * `parent_asset_id`, `kind`, `driver_id`, `credential_id`, `name`, `code`,
 * `lifecycle`, `config` and `notes` from the body, and anything the body omits
 * becomes NULL. The detail route selects a deliberately narrower list — it has
 * no `credential_id` and no `retired_at` — because it exists to render four
 * read-only tabs. So a form seeded from it would send `credentialId: null` on
 * every save and unlink the camera's credentials, and the failure would surface
 * later as a fleet that stopped authenticating.
 *
 * `GET /api/assets/:id` returns `ASSET_COLUMNS`, which is exactly the set the
 * PUT writes. That is the one to edit against, so this pane loads it on open.
 *
 * ⚠️ That pairing is not enforced anywhere. If a column is ever added to the
 * UPDATE, it has to appear in `ASSET_COLUMNS` too or this form starts nulling
 * it — `asset_edit_screen.test.ts` pins the two lists against each other for
 * exactly that reason.
 *
 * ── What is deliberately not re-implemented here ─────────────────────────
 *
 * The server refuses an `active` asset with no address, a driver that cannot
 * answer for the asset's kind, a code that collides case-insensitively, and a
 * parent that is the asset itself. None of those is checked again in the
 * browser. A second copy of a rule is a second thing to keep in step, and the
 * copy that matters is the one the write goes through — so this shows the
 * server's sentence verbatim rather than paraphrasing it.
 *
 * The active-with-no-address refusal in particular is worth reading in full:
 * it names the config keys that would satisfy it.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Field, Loading, Section } from './components/index.ts';
import {
  ADDRESS_KEYS,
  ConfigTextError,
  LIFECYCLES,
  PUSH_ONLY_DRIVERS,
  type AssetRecord,
  type Draft,
  bodyFrom,
  draftOf,
} from './asset-form.ts';

interface SiteOption {
  id: string;
  code: string | null;
  name: string;
}

interface CredentialOption {
  id: string;
  label: string;
  kind: string;
}

interface KindOption {
  kind: string;
  label: string;
}

interface DriverOption {
  id: string;
  asset_kind: string;
  label: string;
  transport: string;
}

export function AssetEditTab({
  assetId,
  onChanged,
}: {
  assetId: string;
  /** Called after a write lands, so the drawer's read-only tabs catch up. */
  onChanged: () => void;
}): React.JSX.Element {
  const [asset, setAsset] = useState<AssetRecord | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [credentials, setCredentials] = useState<CredentialOption[]>([]);
  const [kinds, setKinds] = useState<KindOption[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);

  const load = useCallback(async () => {
    try {
      const record = await api.get<AssetRecord>(`/api/assets/${assetId}`);
      setAsset(record);
      setDraft(draftOf(record));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [assetId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * The option lists are loaded separately and their failure is NOT fatal.
   * A missing site list should leave the rest of the form editable rather than
   * blanking a screen whose main job is the fields above it — and a select that
   * cannot offer the current value still shows it, see `orphan` below.
   */
  useEffect(() => {
    void api
      .get<{ sites: SiteOption[] }>('/api/sites')
      .then((r) => setSites(r.sites))
      .catch(() => setSites([]));
    void api
      .get<{ credentials: CredentialOption[] }>('/api/credentials')
      .then((r) => setCredentials(r.credentials))
      .catch(() => setCredentials([]));
    void api
      .get<{ kinds: KindOption[]; drivers: DriverOption[] }>('/api/catalogue')
      .then((r) => {
        setKinds(r.kinds);
        setDrivers(r.drivers);
      })
      .catch(() => {
        setKinds([]);
        setDrivers([]);
      });
  }, []);

  const edit = useCallback((patch: Partial<Draft>) => {
    setDraft((current) => (current === null ? current : { ...current, ...patch }));
  }, []);

  const save = useCallback(async () => {
    if (draft === null) return;
    /*
     * The one refusal that happens HERE rather than at the server, and it is
     * the exception that proves the rule above: unparseable JSON never becomes
     * a request at all, so there is no server sentence to show. Caught before
     * `setBusy` so a malformed box cannot leave the form stuck on "Working…".
     */
    let body: Record<string, unknown>;
    try {
      body = bodyFrom(draft);
    } catch (err) {
      setError(err instanceof ConfigTextError ? err.message : String(err));
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const result = await api.put<{ unrecognisedConfig?: string[] }>(
        `/api/assets/${assetId}`,
        body,
      );
      /*
       * 🔴 Shown rather than swallowed. The server KEEPS a config key it does
       * not recognise — deliberately, because drivers are still being written
       * and the seeded fleet already carries rtsp_port and rtsp_subtype that
       * nothing reads yet — and reports which. Hiding that would turn a typo
       * like `hots` into a key that is stored forever, read by nothing, and
       * looks exactly like configuration that works.
       */
      const unknown = result.unrecognisedConfig ?? [];
      setNotice(
        unknown.length === 0
          ? 'Saved.'
          : `Saved. Kept ${unknown.length} config key(s) nothing recognises: ${unknown.join(', ')} — ` +
            'check the spelling if one of those was meant to be an address or a port.',
      );
      setError(null);
      await load();
      onChanged();
    } catch (err) {
      // The refusals are the useful part, and they are sentences. The
      // active-with-no-address one names the keys that would satisfy it.
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [assetId, draft, load, onChanged]);

  const retire = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      await api.del(`/api/assets/${assetId}`);
      setNotice('Retired. Its open alerts were resolved with it.');
      setError(null);
      setConfirmRetire(false);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [assetId, load, onChanged]);

  /**
   * Bring a retired asset back.
   *
   * Sends the asset's OWN stored values rather than the draft, exactly as
   * `Sites.tsx` does: the inputs are disabled while it is retired, so the draft
   * is whatever happened to be in the boxes, and a restore should not silently
   * apply an edit nobody asked for. `retired: false` is the only change.
   *
   * ⚠️ A restore to `active` stamps `activated_at` through migration 022, so
   * the asset gets its grace period back and is silent for an hour. That is the
   * intent, and it is worth knowing before wondering why it is not reporting.
   */
  const restore = useCallback(async () => {
    if (asset === null) return;
    setBusy(true);
    setNotice(null);
    try {
      await api.put(`/api/assets/${assetId}`, {
        ...bodyFrom(draftOf(asset)),
        retired: false,
      });
      setNotice('Restored.');
      setError(null);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [asset, assetId, load, onChanged]);

  if (error !== null && draft === null) return <Banner tone="fault">{error}</Banner>;
  if (draft === null || asset === null) return <Loading rows={6} cols={2} label="Loading asset" />;

  const retired = asset.retired_at !== null;
  /*
   * The asset points at a site or credential the list does not offer — another
   * tenant's, or one retired since. Shown as its own option rather than
   * silently resetting the select to "none", which on save would unlink it.
   */
  const siteOrphan = draft.siteId !== '' && !sites.some((s) => s.id === draft.siteId);
  const credentialOrphan =
    draft.credentialId !== '' && !credentials.some((c) => c.id === draft.credentialId);
  /*
   * The catalogue could not be loaded, or this asset carries a kind/driver the
   * catalogue no longer lists. Either way the current value is offered as its
   * own option: a select that silently dropped it would reset the field, and
   * the next save would write the reset rather than the edit somebody made.
   */
  const kindOrphan = draft.kind !== '' && !kinds.some((k) => k.kind === draft.kind);
  const driverOrphan = draft.driverId !== '' && !drivers.some((d) => d.id === draft.driverId);
  /** The chosen driver, when it cannot answer for the chosen kind. */
  const mismatchedDriver = drivers.find(
    (d) => d.id === draft.driverId && d.asset_kind !== draft.kind,
  );

  const locked = retired || busy;

  /*
   * Said here, enforced by the server. This only stops the refusal being a
   * surprise after the click — the sentence the API returns names the config
   * keys that would satisfy it, and is shown in full in the banner.
   */
  // A pushed-only driver is never dialled, so it needs no address (see PUSH_ONLY_DRIVERS).
  const needsAddress =
    draft.lifecycle === 'active' && draft.address.trim() === '' && !PUSH_ONLY_DRIVERS.includes(draft.driverId);

  return (
    <div className="stack">
      {error !== null && <Banner tone="fault">{error}</Banner>}
      {notice !== null && <Banner tone="ok">{notice}</Banner>}

      {retired && (
        <Banner tone="warn" title="This asset is retired">
          It is not polled and cannot be edited until it is restored.
        </Banner>
      )}

      <Section title="Identity">
        <Field id="asset-name" label="Name">
          <input
            id="asset-name"
            value={draft.name}
            onChange={(e) => edit({ name: e.target.value })}
            disabled={locked}
          />
        </Field>

        <Field
          id="asset-code"
          label="Code"
          hint="The key on a job sheet. Compared case-insensitively, so CK1048 and ck1048 are one asset."
        >
          <input
            id="asset-code"
            value={draft.code}
            onChange={(e) => edit({ code: e.target.value })}
            disabled={locked}
          />
        </Field>

        <Field id="asset-site" label="Site">
          <select
            id="asset-site"
            value={draft.siteId}
            onChange={(e) => edit({ siteId: e.target.value })}
            disabled={locked}
          >
            <option value="">— none —</option>
            {siteOrphan && <option value={draft.siteId}>current site (not in this list)</option>}
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code === null ? s.name : `${s.code} — ${s.name}`}
              </option>
            ))}
          </select>
        </Field>

        <Field
          id="asset-lifecycle"
          label="Lifecycle"
          {...(needsAddress
            ? { hint: 'An active asset needs an address, or the collector cannot dial it and it records nothing while appearing on the fleet.' }
            : {})}
        >
          <select
            id="asset-lifecycle"
            value={draft.lifecycle}
            onChange={(e) => edit({ lifecycle: e.target.value })}
            disabled={locked}
          >
            {LIFECYCLES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>
      </Section>

      <Section title="Connection">
        <Field id="asset-kind" label="Kind">
          <select
            id="asset-kind"
            value={draft.kind}
            onChange={(e) => edit({ kind: e.target.value })}
            disabled={locked}
          >
            {kindOrphan && <option value={draft.kind}>{draft.kind} (not in the catalogue)</option>}
            {kinds.map((k) => (
              <option key={k.kind} value={k.kind}>
                {k.label} ({k.kind})
              </option>
            ))}
          </select>
        </Field>

        {/*
          🔴 The list is NOT filtered to the current kind, and that is
          deliberate. Nothing in the schema relates `assets.kind` to
          `assets.driver_id` — `drivers.asset_kind` is used by no constraint —
          so a mismatched pair is something the database will happily store and
          the ROUTE refuses. Hiding the wrong ones would make the form quietly
          disagree with the write, and hide the fact that the relation is a
          convention rather than a constraint. They are shown, labelled with
          what they are actually for, and the server has the last word.
        */}
        <Field
          id="asset-driver"
          label="Driver"
          error={
            mismatchedDriver === undefined
              ? null
              : `${mismatchedDriver.id} is for a ${mismatchedDriver.asset_kind}, not a ${draft.kind}. The server will refuse this — every check on the asset would resolve unavailable.`
          }
        >
          <select
            id="asset-driver"
            value={draft.driverId}
            onChange={(e) => edit({ driverId: e.target.value })}
            disabled={locked}
          >
            <option value="">— none —</option>
            {driverOrphan && (
              <option value={draft.driverId}>{draft.driverId} (not in the catalogue)</option>
            )}
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.id} — {d.label}
                {d.asset_kind === draft.kind ? '' : ` · for a ${d.asset_kind}`}
              </option>
            ))}
          </select>
        </Field>

        <Field id="asset-credential" label="Credential">
          <select
            id="asset-credential"
            value={draft.credentialId}
            onChange={(e) => edit({ credentialId: e.target.value })}
            disabled={locked}
          >
            <option value="">— none —</option>
            {credentialOrphan && (
              <option value={draft.credentialId}>current credential (not in this list)</option>
            )}
            {credentials.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label} ({c.kind})
              </option>
            ))}
          </select>
        </Field>

        <Field
          id="asset-address"
          label="Address"
          hint={`The collector tries ${ADDRESS_KEYS.join(', ')} in that order; the key this asset already had is kept.`}
        >
          <span className="row">
            <select
              aria-label="Address key"
              value={draft.addressKey}
              onChange={(e) => edit({ addressKey: e.target.value })}
              disabled={locked}
            >
              {ADDRESS_KEYS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <input
              id="asset-address"
              value={draft.address}
              onChange={(e) => edit({ address: e.target.value })}
              disabled={locked}
            />
          </span>
        </Field>

        <Field id="asset-port" label="Port">
          <input
            id="asset-port"
            value={draft.port}
            onChange={(e) => edit({ port: e.target.value })}
            disabled={locked}
            placeholder="80"
          />
        </Field>
      </Section>

      <Section title="Notes and config">
        <Field id="asset-notes" label="Notes">
          <textarea
            id="asset-notes"
            value={draft.notes}
            onChange={(e) => edit({ notes: e.target.value })}
            disabled={locked}
            rows={3}
          />
        </Field>

        <Field
          id="asset-config"
          label="Other config (JSON)"
          hint="Merged with the address and port above. Keys nothing recognises are kept and named back on save."
        >
          <textarea
            id="asset-config"
            value={draft.extraConfig}
            onChange={(e) => edit({ extraConfig: e.target.value })}
            disabled={locked}
            rows={5}
            spellCheck={false}
          />
        </Field>
      </Section>

      {!retired && asset !== null && (
        <AssetExemptions asset={asset} onChanged={() => { void load(); onChanged(); }} />
      )}

      <div className="row">
        {!retired && (
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {busy ? 'Working…' : 'Save'}
          </Button>
        )}
        {retired ? (
          <Button variant="primary" busy={busy} onClick={() => void restore()}>
            Restore
          </Button>
        ) : confirmRetire ? (
          <>
            {/*
              Two clicks, and the sentence between them says what else happens.
              Retiring resolves every alert open about the asset — correct, since
              an alert about a camera nobody has any more can never clear on its
              own — and not something to discover afterwards.
            */}
            <span className="small">
              Retire this asset? Its open alerts are resolved with it. Reversible.
            </span>
            <Button variant="danger" busy={busy} onClick={() => void retire()}>
              Confirm retire
            </Button>
            <Button variant="ghost" onClick={() => setConfirmRetire(false)} disabled={busy}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="danger" onClick={() => setConfirmRetire(true)} disabled={busy}>
            Retire
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The two exemptions, and why they are not part of the form above.
 *
 * 🔴 Each has its OWN route and its own button because the asset PUT rewrites
 * every column. Folding them into the main save would mean an operator editing
 * a camera's address silently re-asserting an exemption — or clearing one —
 * from whatever the form happened to be holding. `PUT /api/assets/:id` is a
 * full replace; these two are single-purpose.
 *
 * They were API-only until 2026-09-16. Both suppress a fault, and a suppression
 * that can only be set with a curl command is one nobody can audit by looking.
 *
 * ── Neither resolves a standing alert here ───────────────────────────────
 *
 * The next collector pass does it, through the same exemption the engine
 * applies when deciding not to raise. One code path, so the flag cannot mean
 * one thing at write time and another at evaluation time — and the screen says
 * so rather than leaving somebody watching an alert that has not moved.
 */
function AssetExemptions({
  asset,
  onChanged,
}: {
  asset: AssetRecord;
  onChanged: () => void;
}): React.JSX.Element {
  const recordsOnboard = asset.records_onboard !== false;
  const [onboardReason, setOnboardReason] = useState(asset.onboard_exempt_reason ?? '');
  const [encodingReason, setEncodingReason] = useState(asset.encoding_exempt_reason ?? '');
  const [busy, setBusy] = useState<'onboard' | 'encoding' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const encodingExempt = (asset.encoding_exempt_reason ?? '') !== '';

  async function write(which: 'onboard' | 'encoding', path: string, body: unknown, done: string): Promise<void> {
    setBusy(which);
    setError(null);
    try {
      await api.put(path, body);
      setNotice(done);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Section
      title="Exemptions"
      summary="Each is a decision about this deployment, not about the device. Both need a reason, and both take effect on the next pass rather than closing an open alert now. A host's single-drive switch is on its Status tab, beside the reading."
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {notice !== null && <Banner tone="ok">{notice}</Banner>}

        <Field
          id="onboard-reason"
          label="Onboard recording"
          hint={
            recordsOnboard
              ? 'Expected to record to its own card. Give a reason to exempt it.'
              : `Exempt. ${asset.onboard_exempt_reason ?? 'No reason recorded.'}`
          }
        >
          <input
            id="onboard-reason"
            type="text"
            value={onboardReason}
            placeholder="records to the NVR at the gate"
            onChange={(event) => {
              setOnboardReason(event.target.value);
              setNotice(null);
            }}
          />
        </Field>
        <div className="row">
          <Button
            variant="danger"
            size="sm"
            busy={busy === 'onboard'}
            disabled={!recordsOnboard || onboardReason.trim() === ''}
            onClick={() =>
              void write(
                'onboard',
                `/api/assets/${asset.id}/onboard-recording`,
                { recordsOnboard: false, reason: onboardReason.trim() },
                'Exempt. Any standing alert closes on the next pass.',
              )
            }
          >
            Exempt
          </Button>
          <Button
            variant="ghost"
            size="sm"
            busy={busy === 'onboard'}
            disabled={recordsOnboard}
            onClick={() =>
              void write(
                'onboard',
                `/api/assets/${asset.id}/onboard-recording`,
                { recordsOnboard: true },
                'Held to onboard recording again.',
              )
            }
          >
            Hold to the rule
          </Button>
        </div>

        <Field
          id="encoding-reason"
          label="Encoding spec"
          hint={
            encodingExempt
              ? `Exempt. ${asset.encoding_exempt_reason ?? ''}`
              : 'Held to the substream spec. A reason exempts it — for a camera whose firmware cannot meet it, or one nobody intends to back up.'
          }
        >
          <input
            id="encoding-reason"
            type="text"
            value={encodingReason}
            placeholder="firmware has no H.265 substream"
            onChange={(event) => {
              setEncodingReason(event.target.value);
              setNotice(null);
            }}
          />
        </Field>
        <div className="row">
          <Button
            variant="danger"
            size="sm"
            busy={busy === 'encoding'}
            disabled={encodingReason.trim() === ''}
            onClick={() =>
              void write(
                'encoding',
                `/api/assets/${asset.id}/encoding-exemption`,
                { reason: encodingReason.trim() },
                'Exempt from the encoding spec.',
              )
            }
          >
            Exempt
          </Button>
          <Button
            variant="ghost"
            size="sm"
            busy={busy === 'encoding'}
            disabled={!encodingExempt}
            onClick={() =>
              void write(
                'encoding',
                `/api/assets/${asset.id}/encoding-exemption`,
                { reason: null },
                'Held to the encoding spec again.',
              )
            }
          >
            Hold to the spec
          </Button>
        </div>
      </div>
    </Section>
  );
}
